/**
 * Account Information Screen
 *
 * Displays user account details in a native web interface (not an MFE).
 * Mirrors the implementation from lore-native/src/screens/menu/AccountInformationScreen.web.tsx
 *
 * Features:
 * - Account Details: Email Address, Display Name
 * - Actions: Sign out, Delete LoreBot data, Request account deletion
 *
 * @module components/account/AccountScreen
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { UserApiService } from '@/services/api';
import { Button, Spinner } from '@sequelae/lore-core';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import './AccountInformationScreen.css';

export const AccountInformationScreen: React.FC = () => {
  const { user, logout } = useAuth();
  const { profile, isLoading: profileLoading } = useProfile();
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const email = profile?.email || user?.email || 'Not available';
  const displayName = profile?.lore_user_name || 'Not available';

  const handleSignOutClick = () => setShowSignOutModal(true);

  const handleSignOutConfirm = async () => {
    setShowSignOutModal(false);
    try {
      await logout();
      console.log('[AccountScreen] Sign out successful, redirecting to app.lorehealthcare.com...');
      window.location.href = 'https://app.lorehealthcare.com/';
    } catch (error) {
      console.error('[AccountScreen] Sign out error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  const handleSignOutCancel = () => setShowSignOutModal(false);

  const handleDeleteLoreBotData = async () => {
    if (!user?.id) return;

    if (
      window.confirm(
        'Are you sure you want to delete all your LoreBot conversation data? This cannot be undone.'
      )
    ) {
      try {
        await UserApiService.deleteLoreBotData(user.id);
        alert('LoreBot data has been deleted successfully.');
      } catch (error) {
        console.error('[AccountScreen] Error deleting LoreBot data:', error);
        alert('Failed to delete LoreBot data. Please try again.');
      }
    }
  };

  const handleRequestAccountDeletion = async () => {
    if (!user?.id) return;

    if (
      window.confirm(
        'Are you sure you want to request account deletion? This action cannot be undone and will permanently delete all your data.'
      )
    ) {
      try {
        await UserApiService.requestAccountDeletion(user.id);
        alert(
          'Account deletion request has been submitted. You will be signed out.'
        );
        await logout();
      } catch (error) {
        console.error('[AccountScreen] Error requesting account deletion:', error);
        alert('Failed to request account deletion. Please try again.');
      }
    }
  };

  if (profileLoading) {
    return (
      <div className="account-screen-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="account-screen">
      <div className="account-sections">
        {/* Account Details */}
        <div className="account-section">
          <h4>Account Details</h4>
          <div className="info-row">
            <span className="info-label">Email Address</span>
            <span className="info-value">{email}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Display Name</span>
            <span className="info-value">{displayName}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="account-section">
          <h4>Actions</h4>
          <div className="action-buttons">
            <Button onClick={handleSignOutClick} variant="secondary" size="medium" fullWidth>
              Sign Out
            </Button>
            <Button onClick={handleDeleteLoreBotData} variant="secondary" size="medium" fullWidth>
              Delete LoreBot Data
            </Button>
            <Button
              onClick={handleRequestAccountDeletion}
              variant="secondary"
              size="medium"
              fullWidth
              style={{ color: '#e53e3e' }}
            >
              Request Account Deletion
            </Button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={showSignOutModal}
        title="Sign out"
        message="Are you sure you want to sign out?"
        confirmLabel="Sign out"
        cancelLabel="Cancel"
        onConfirm={handleSignOutConfirm}
        onCancel={handleSignOutCancel}
      />
    </div>
  );
};
